import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { baseUrl } from 'libs/core/src/lib/configs/baseUrl';
import {
  createQueryParams,
  IApiResult,
  IPaymentAttempt,
  IPaymentResponse,
  IPolicy,
  IStatusStats,
  IVoucherPaymentResponse,
  IWalletBalance,
  IWalletBalanceParams,
  PaginatedResponse,
  PaymentMethod,
  PaymentMethodsParams,
  PaymentsQueryParams,
} from '@inshuwa-client/models';
import { delay, Observable, repeat, takeUntil, takeWhile, timer } from 'rxjs';
import { NgHttpCachingHeaders } from 'ng-http-caching';

@Injectable({
  providedIn: 'root',
})
export class PaymentsService {
  constructor(private readonly http: HttpClient) {}

  //check transaction status every 20 seconds for 3 minutes by default
  //if transaction is not completed after 3 minutes, stop checking
  pullTransactionStatus(
    txnId: string,
    timoutDuration = 300000,
  ): Observable<IPaymentResponse> {
    const timer$ = timer(timoutDuration);
    const api$ = this.http.get<IPaymentResponse>(
      `${baseUrl()}/payments/status/${txnId}`,
      {
        headers: {
          [NgHttpCachingHeaders.DISALLOW_CACHE]: '1',
        },
      },
    );
    return api$
      .pipe(delay(5000), repeat(), takeUntil(timer$))
      .pipe(takeWhile((value) => value.status === 'Pending', true));
  }

  getPolicyRedirectedPolicy(): Observable<IPolicy> {
    return this.http.get<IPolicy>(`${baseUrl()}/transactions/policy/redirect`, {
      headers: {
        'x-redirection-url': window.location.href,
      },
    });
  }

  getPayments(
    param?: Partial<PaymentsQueryParams>,
    context?: HttpContext,
  ): Observable<PaginatedResponse<IVoucherPaymentResponse>> {
    return this.http.get<PaginatedResponse<IVoucherPaymentResponse>>(
      `${baseUrl()}/payments`,
      {
        params: createQueryParams(param),
        context,
      },
    );
  }

  getPaymentStats(
    param?: Partial<PaymentsQueryParams>,
    context?: HttpContext,
  ): Observable<IStatusStats> {
    return this.http.get<IStatusStats>(`${baseUrl()}/payments/stats`, {
      params: createQueryParams(param),
      context,
    });
  }

  getPaymentMethods(
    param?: Partial<PaymentMethodsParams>,
  ): Observable<PaymentMethod[]> {
    return this.http.get<PaymentMethod[]>(`${baseUrl()}/payments/methods`, {
      params: createQueryParams(param),
    });
  }

  processPayment(id: string): Observable<IApiResult> {
    return this.http.post<IApiResult>(`${baseUrl()}/payments/refund/${id}`, {});
  }

  /** Re-sends a failed internal wallet transfer under the same reference; Lipila is checked first. */
  retryWalletTransfer(id: string): Observable<IApiResult> {
    return this.http.post<IApiResult>(`${baseUrl()}/lipila/transfers/${id}/retry`, {});
  }

  /** Every attempt to send an internal wallet transfer, newest first. */
  getWalletTransferAttempts(id: string): Observable<IPaymentAttempt[]> {
    return this.http.get<IPaymentAttempt[]>(`${baseUrl()}/lipila/transfers/${id}/attempts`);
  }

  clientWalletBalance(
    params?: Partial<IWalletBalanceParams>,
  ): Observable<IWalletBalance> {
    return this.http.get<IWalletBalance>(`${baseUrl()}/wallet/balance`, {
      params: createQueryParams(params),
    });
  }
}
